import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Briefcase,
  Calendar,
  DollarSign,
  MapPin,
  Bookmark,
  X,
  Building,
  Clock,
  Users,
  Globe,
  ExternalLink,
  Play,
  TrendingUp,
  GraduationCap,
  Book,
  Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';

const careerPaths = [
  {
    id: 'data-science',
    title: 'Data Science',
    description: 'Analyze and interpret complex data to help organizations make better decisions.',
    avgSalary: '$120,000',
    education: "Bachelor's/Master's in Computer Science, Statistics, or related field",
    experience: '3-5 years',
    skills: ['Python', 'Machine Learning', 'SQL', 'Statistics'],
    outlook: 'High growth (25% over 10 years)',
    environment: 'Hybrid/Remote',
    satisfaction: '4.2/5',
    matchScore: 95
  },
  {
    id: 'software-dev',
    title: 'Software Development',
    description: 'Design and build software applications and systems.',
    avgSalary: '$110,000',
    education: "Bachelor's in Computer Science or related field",
    experience: '2-4 years',
    skills: ['JavaScript', 'Python', 'React', 'Node.js'],
    outlook: 'Very High growth (30% over 10 years)',
    environment: 'Hybrid/Remote',
    satisfaction: '4.1/5',
    matchScore: 92
  },
  {
    id: 'product-manager',
    title: 'Product Management',
    description: 'Lead product development and strategy.',
    avgSalary: '$125,000',
    education: "Bachelor's in Business/Technology + MBA",
    experience: '4-6 years',
    skills: ['Strategy', 'Analytics', 'Leadership', 'Technical Knowledge'],
    outlook: 'High growth (20% over 10 years)',
    environment: 'Hybrid',
    satisfaction: '4.3/5',
    matchScore: 88
  },
  {
    id: 'ux-designer',
    title: 'UX Designer',
    description: 'Design user experiences for digital products.',
    avgSalary: '$95,000',
    education: "Bachelor's in Design, HCI, or related field",
    experience: '2-4 years',
    skills: ['UI Design', 'User Research', 'Prototyping', 'Figma'],
    outlook: 'High growth (22% over 10 years)',
    environment: 'Hybrid/Remote',
    satisfaction: '4.0/5',
    matchScore: 85
  }
];

const CareerComparison: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCareers, setSelectedCareers] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const filteredCareers = careerPaths.filter(career =>
    career.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleSelect = (pathId: string) => {
    setSelectedCareers(prev => {
      if (prev.includes(pathId)) {
        return prev.filter(id => id !== pathId);
      }
      if (prev.length < 3) {
        return [...prev, pathId];
      }
      return prev;
    });
  };

  const selectedCareerDetails = careerPaths.filter(career => 
    selectedCareers.includes(career.id)
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <div className="relative bg-gradient-to-b from-primary-900 to-primary-800 py-16 mb-12 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-700 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-accent-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
                  Compare Career Paths
                </h1>
                <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8">
                  Select up to three career paths to compare and find your perfect match.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    <span>Growth Potential</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <DollarSign className="h-5 w-5 mr-2" />
                    <span>Salary Insights</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Globe className="h-5 w-5 mr-2" />
                    <span>Global Opportunities</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Search Bar */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1 w-full">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      placeholder="Search career paths..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input pl-12 w-full bg-gray-50 border-0 focus:ring-2"
                    />
                  </div>
                </div>
                <Button
                  variant={showFilters ? 'primary' : 'outline'}
                  onClick={() => setShowFilters(!showFilters)}
                  icon={<Filter className="h-4 w-4" />}
                >
                  Filters
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Career Selection - Horizontal Layout */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredCareers.map((career) => (
                <div
                  key={career.id}
                  className={`bg-white rounded-xl p-6 shadow-md transition-all duration-200 cursor-pointer ${
                    selectedCareers.includes(career.id) ? 'ring-2 ring-primary-500' : ''
                  }`}
                  onClick={() => handleToggleSelect(career.id)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{career.title}</h3>
                      <div className="text-sm text-gray-500">{career.matchScore}% match</div>
                    </div>
                    <Button
                      variant={selectedCareers.includes(career.id) ? 'primary' : 'outline'}
                      size="sm"
                    >
                      {selectedCareers.includes(career.id) ? 'Selected' : 'Select'}
                    </Button>
                  </div>
                  <p className="text-gray-600 text-sm">{career.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Selected Career Details */}
          {selectedCareerDetails.length > 0 && (
            <motion.div variants={itemVariants} className="space-y-8">
              {/* Career Overview */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Career Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {selectedCareerDetails.map(career => (
                    <div key={career.id} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">{career.title}</h3>
                        <span className="text-sm font-medium text-primary-600">{career.matchScore}% match</span>
                      </div>
                      <p className="text-gray-600">{career.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comparison Details */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Detailed Comparison</h2>
                
                {/* Salary Comparison */}
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <DollarSign className="h-5 w-5 text-primary-600 mr-2" />
                      Average Salary
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedCareerDetails.map(career => (
                        <div key={career.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="font-medium text-gray-900">{career.title}</div>
                          <div className="text-2xl font-bold text-primary-600">{career.avgSalary}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Education Requirements */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <GraduationCap className="h-5 w-5 text-primary-600 mr-2" />
                      Education Requirements
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedCareerDetails.map(career => (
                        <div key={career.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="font-medium text-gray-900">{career.title}</div>
                          <div className="text-gray-600">{career.education}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Experience */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Briefcase className="h-5 w-5 text-primary-600 mr-2" />
                      Experience Required
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedCareerDetails.map(career => (
                        <div key={career.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="font-medium text-gray-900">{career.title}</div>
                          <div className="text-gray-600">{career.experience}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Skills */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Star className="h-5 w-5 text-primary-600 mr-2" />
                      Key Skills
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedCareerDetails.map(career => (
                        <div key={career.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="font-medium text-gray-900 mb-2">{career.title}</div>
                          <div className="flex flex-wrap gap-2">
                            {career.skills.map((skill, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Job Outlook */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <TrendingUp className="h-5 w-5 text-primary-600 mr-2" />
                      Job Outlook
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedCareerDetails.map(career => (
                        <div key={career.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="font-medium text-gray-900">{career.title}</div>
                          <div className="text-gray-600">{career.outlook}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Work Environment */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Building className="h-5 w-5 text-primary-600 mr-2" />
                      Work Environment
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedCareerDetails.map(career => (
                        <div key={career.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="font-medium text-gray-900">{career.title}</div>
                          <div className="text-gray-600">{career.environment}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Career Satisfaction */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Star className="h-5 w-5 text-primary-600 mr-2" />
                      Career Satisfaction
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedCareerDetails.map(career => (
                        <div key={career.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="font-medium text-gray-900">{career.title}</div>
                          <div className="text-gray-600">{career.satisfaction}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Career Simulator Button */}
          <div className="fixed right-8 bottom-8">
            <Button
              onClick={() => navigate('/career-simulator')}
              className="bg-primary-600 text-white px-6 py-3 rounded-xl text-lg font-semibold shadow-lg hover:bg-primary-700 transition-all duration-300 flex items-center gap-2"
              icon={<Play className="h-5 w-5" />}
            >
              Career Simulator
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CareerComparison;